import React, { useState, useEffect, useRef, ChangeEvent } from "react";

type ImageUploaderProps = {
  value?: string | File | null;
  onImageSelected: (image: File | null) => void;
  onImageClear?: () => void;
  width?: number | string;
  height?: number | string;
  disabled?: boolean;
  className?: string;
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  value,
  onImageSelected,
  onImageClear,
  width = 100,
  height = 100,
  disabled = false,
  className = "",
}) => {
  const [preview, setPreview] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let objectUrl = "";

    if (value instanceof File) {
      objectUrl = URL.createObjectURL(value);
      setPreview(objectUrl);
    } else if (typeof value === "string" && value.trim() !== "") {
      setPreview(value);
    } else {
      setPreview("");
    }

    // Clean up blob URL to prevent memory leaks
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [value]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onImageSelected(file);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (onImageClear) {
      onImageClear();
    } else {
      onImageSelected(null);
    }
  };

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width,
        height,
        border: "1px dashed #ccc",
        borderRadius: 8,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9fafb",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {preview ? (
        <>
          <img
            src={preview}
            alt="preview"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Remove image"
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                backgroundColor: "rgba(0, 0, 0, 0.65)",
                border: "none",
                color: "#fff",
                borderRadius: "50%",
                width: 22,
                height: 22,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </>
      ) : (
        <>
          {!disabled && (
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{
                width: "100%",
                height: "100%",
                opacity: 0,
                position: "absolute",
                top: 0,
                left: 0,
                cursor: "pointer",
              }}
            />
          )}
          <span
            style={{
              color: "#9ca3af",
              fontSize: 12,
              textAlign: "center",
              padding: "0 4px",
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            Click to upload
          </span>
        </>
      )}
    </div>
  );
};

export default ImageUploader;